import React, { useState, useEffect } from 'react';

// ============================================================================
// 5-DAY ACTION PLAN BUILDER
// A streamlined wizard that creates a specific weekly action plan
// with AI prompts to help agents execute
// ============================================================================

const LEAD_MEASURE_OPTIONS = {
  databaseCalls: {
    label: 'Database/Sphere Calls',
    unit: 'calls',
    dailyDefault: 10,
    description: 'Call past clients and people who know you',
    aiPrompts: [
      {
        title: 'Warm reconnection script',
        prompt: 'Write me a natural, friendly phone script to reconnect with past clients I haven\'t spoken to in 6+ months. Keep it conversational, not salesy. Include a genuine reason for calling and a soft way to ask about their real estate plans.',
      },
      {
        title: 'Creative reasons to call',
        prompt: 'Give me 10 creative, genuine reasons to call people in my real estate sphere of influence that aren\'t "just checking in" or "touching base." I want reasons that provide value or show I\'m thinking of them specifically.',
      },
      {
        title: 'Voicemail script',
        prompt: 'Write me a 20-second voicemail script for sphere calls that sounds natural, gives them a reason to call back, and doesn\'t sound like every other agent. Make it memorable.',
      },
      {
        title: 'Handle "not ready yet"',
        prompt: 'Help me respond to a past client who says "we\'re not thinking about moving right now" in a way that keeps the relationship warm, provides value, and sets up a natural future follow-up without being pushy.',
      },
      {
        title: 'Post-call follow-up text',
        prompt: 'Write me a brief, friendly follow-up text to send after a sphere call that thanks them for chatting, references something personal we discussed, and keeps the door open for real estate conversations.',
      },
    ],
  },
  openHouses: {
    label: 'Open House Conversations',
    unit: 'conversations',
    dailyDefault: 15,
    description: 'Talk to visitors at open houses',
    aiPrompts: [
      {
        title: 'Open house greeting',
        prompt: 'Write me a warm, engaging greeting script for when visitors walk into my open house. I want to build rapport quickly, find out if they\'re working with an agent, and understand their timeline without feeling like an interrogation.',
      },
      {
        title: 'Qualify buyers naturally',
        prompt: 'Give me 5 conversational questions to ask open house visitors that help me understand if they\'re serious buyers, their timeline, and if they\'re pre-approved - without sounding like I\'m qualifying them.',
      },
      {
        title: 'Follow-up email sequence',
        prompt: 'Write me a 3-email follow-up sequence for open house visitors: Email 1 (same day), Email 2 (3 days later), Email 3 (1 week later). Make each one provide value and move them toward scheduling a buyer consultation.',
      },
      {
        title: 'Handle "just looking"',
        prompt: 'Help me respond when an open house visitor says "we\'re just looking" or "we\'re not ready to buy yet" in a way that keeps them engaged, provides value, and captures their info naturally.',
      },
      {
        title: 'Neighbor door knock script',
        prompt: 'Write me a door knock script for the neighbors around my open house listing. I want to invite them to the open house, see if they know anyone looking to move to the area, and plant seeds for future listing opportunities.',
      },
    ],
  },
  doorKnocking: {
    label: 'Door Knocking',
    unit: 'doors',
    dailyDefault: 25,
    description: 'Farm a geographic area',
    aiPrompts: [
      {
        title: 'Initial door knock script',
        prompt: 'Write me a friendly, non-pushy door knocking script for geographic farming. I want to introduce myself as the neighborhood real estate expert, provide immediate value, and ask a question that gets them talking about real estate.',
      },
      {
        title: 'Handle "not interested"',
        prompt: 'Give me 3 different responses for when someone at the door says "not interested" or "we\'re not selling." I want to leave on a positive note and plant a seed for future contact without being pushy.',
      },
      {
        title: 'Leave-behind note',
        prompt: 'Write me a brief, handwritten-style note to leave when no one answers the door. It should be personal, memorable, and give them a reason to keep it rather than throw it away.',
      },
      {
        title: 'Market update conversation',
        prompt: 'Help me explain the current local real estate market to a homeowner in 30 seconds or less. Include recent sales data talking points and a hook that makes them curious about their home\'s value.',
      },
      {
        title: 'Second visit script',
        prompt: 'Write me a door knock script for returning to homes I\'ve already visited once. I want to build on the previous interaction, provide new value, and deepen the relationship.',
      },
    ],
  },
  paidLeadFollowUp: {
    label: 'Paid Lead Follow-up',
    unit: 'calls',
    dailyDefault: 15,
    description: 'Call Zillow, Realtor.com, or other leads',
    aiPrompts: [
      {
        title: 'Speed-to-lead script',
        prompt: 'Write me a phone script for calling a new online lead within 5 minutes of them inquiring. I want to acknowledge the property they looked at, qualify their timeline, and book an appointment - all in under 2 minutes.',
      },
      {
        title: 'Day 2-7 follow-up sequence',
        prompt: 'Create a call/text follow-up sequence for online leads who didn\'t answer my first call. Give me scripts for days 2, 3, 5, and 7 that don\'t sound desperate but keep me top of mind.',
      },
      {
        title: 'Re-engage cold leads',
        prompt: 'Write me a script to call online leads from 30-90 days ago who went cold. I need a pattern interrupt that gets their attention and re-qualifies their interest without being annoying.',
      },
      {
        title: 'Text message templates',
        prompt: 'Give me 5 different text message templates for following up with online leads who won\'t answer the phone. Each should be brief, provide value, and include a clear call to action.',
      },
      {
        title: 'Handle "just browsing online"',
        prompt: 'Help me respond when an online lead says "I was just browsing" or "I\'m not really looking." I want to understand their real timeline and keep them engaged without being pushy.',
      },
    ],
  },
  socialMedia: {
    label: 'Social Media Outreach',
    unit: 'DMs/comments',
    dailyDefault: 20,
    description: 'Engage and DM on social platforms',
    aiPrompts: [
      {
        title: 'DM conversation starters',
        prompt: 'Give me 10 natural, non-salesy ways to start a DM conversation with someone who engaged with my real estate content. I want to build relationship first, not pitch immediately.',
      },
      {
        title: 'Comment strategy',
        prompt: 'Help me write engaging comments on my sphere\'s social media posts that are genuine, memorable, and keep me top of mind without being about real estate. Give me templates for different types of posts (vacation photos, life updates, accomplishments).',
      },
      {
        title: 'Content ideas for engagement',
        prompt: 'Give me 10 social media post ideas that will generate comments and DMs from potential buyers and sellers. Focus on posts that start conversations, not just get likes.',
      },
      {
        title: 'Transition to real estate',
        prompt: 'Help me naturally transition a social media conversation from casual chatting to real estate. Give me 3 different approaches that don\'t feel forced or salesy.',
      },
      {
        title: 'Story engagement strategy',
        prompt: 'Create a strategy for using Instagram/Facebook stories to generate inbound real estate conversations. Include what to post, how to use polls/questions, and how to follow up with people who engage.',
      },
    ],
  },
  fsboExpireds: {
    label: 'FSBO/Expired Calls',
    unit: 'calls',
    dailyDefault: 20,
    description: 'Call for-sale-by-owners and expired listings',
    aiPrompts: [
      {
        title: 'FSBO initial script',
        prompt: 'Write me a FSBO prospecting script that acknowledges they want to sell themselves, offers genuine help, and positions me as a resource rather than competition. Include a hook that gets them talking.',
      },
      {
        title: 'Expired listing script',
        prompt: 'Write me a script for calling expired listings that doesn\'t blame their previous agent, shows empathy for their frustration, and presents a fresh approach to getting their home sold.',
      },
      {
        title: 'Handle "bad agent experience"',
        prompt: 'Help me respond when an expired listing seller says they had a bad experience with their last agent and don\'t trust Realtors. I want to acknowledge their frustration and differentiate myself.',
      },
      {
        title: 'FSBO value proposition',
        prompt: 'Give me 5 compelling statistics and talking points that show FSBOs the value of working with an agent, presented in a way that\'s educational rather than salesy.',
      },
      {
        title: 'Follow-up sequence',
        prompt: 'Create a 2-week follow-up sequence for FSBOs and expireds who didn\'t list with me on the first call. Include call scripts, text messages, and a value-add piece to mail them.',
      },
    ],
  },
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const TIME_SLOTS = [
  { value: '6:00 AM', label: '6:00 AM' },
  { value: '6:30 AM', label: '6:30 AM' },
  { value: '7:00 AM', label: '7:00 AM' },
  { value: '7:30 AM', label: '7:30 AM' },
  { value: '8:00 AM', label: '8:00 AM' },
  { value: '8:30 AM', label: '8:30 AM' },
  { value: '9:00 AM', label: '9:00 AM' },
  { value: '9:30 AM', label: '9:30 AM' },
  { value: '10:00 AM', label: '10:00 AM' },
  { value: '10:30 AM', label: '10:30 AM' },
  { value: '11:00 AM', label: '11:00 AM' },
  { value: '11:30 AM', label: '11:30 AM' },
  { value: '12:00 PM', label: '12:00 PM' },
  { value: '12:30 PM', label: '12:30 PM' },
  { value: '1:00 PM', label: '1:00 PM' },
  { value: '1:30 PM', label: '1:30 PM' },
  { value: '2:00 PM', label: '2:00 PM' },
  { value: '2:30 PM', label: '2:30 PM' },
  { value: '3:00 PM', label: '3:00 PM' },
  { value: '3:30 PM', label: '3:30 PM' },
  { value: '4:00 PM', label: '4:00 PM' },
  { value: '4:30 PM', label: '4:30 PM' },
  { value: '5:00 PM', label: '5:00 PM' },
  { value: '5:30 PM', label: '5:30 PM' },
  { value: '6:00 PM', label: '6:00 PM' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FiveDayPlanner() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1: Goal
    goalType: 'transactions',
    goalNumber: '',
    avgCommission: 7500,

    // Step 2: Activity
    leadMeasureType: '',
    dailyTarget: '',

    // Step 3: Schedule
    prospectingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    startTime: '8:00 AM',
    duration: '90', // minutes

    // Step 4: Contact
    firstName: '',
    lastName: '',
    email: '',
  });

  const totalSteps = 5; // 0-4 (intro through result)

  // Calculate the math when goal changes
  const calculateMath = () => {
    const goalNum = parseInt(formData.goalNumber) || 0;
    let transactionsNeeded = goalNum;

    if (formData.goalType === 'gci') {
      transactionsNeeded = Math.ceil(goalNum / (formData.avgCommission || 7500));
    }

    // Rough conversion math: 50 conversations → 4 appointments → 1 transaction
    const conversationsNeeded = transactionsNeeded * 50;
    const weeklyConversations = Math.ceil(conversationsNeeded / 13);
    const dailyConversations = Math.ceil(weeklyConversations / 5);

    return {
      transactionsNeeded,
      conversationsNeeded,
      weeklyConversations,
      dailyConversations,
    };
  };

  const math = calculateMath();

  // Get next Monday's date
  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    return nextMonday;
  };

  // Generate the 5-day plan with dates
  const generateWeekPlan = () => {
    const monday = getNextMonday();
    const plan = [];
    const activity = LEAD_MEASURE_OPTIONS[formData.leadMeasureType];
    const dailyTarget = parseInt(formData.dailyTarget) || activity?.dailyDefault || 10;

    DAYS_OF_WEEK.forEach((day, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);

      const isActiveDay = formData.prospectingDays.includes(day);

      plan.push({
        day,
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date,
        isActive: isActiveDay,
        target: isActiveDay ? dailyTarget : 0,
        time: isActiveDay ? formData.startTime : null,
        duration: isActiveDay ? formData.duration : null,
        activity: activity?.label || 'Prospecting',
        unit: activity?.unit || 'activities',
      });
    });

    return plan;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleProspectingDay = (day) => {
    setFormData(prev => {
      const current = prev.prospectingDays;
      if (current.includes(day)) {
        return { ...prev, prospectingDays: current.filter(d => d !== day) };
      } else {
        return { ...prev, prospectingDays: [...current, day] };
      }
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true; // Intro
      case 1: return formData.goalNumber && parseInt(formData.goalNumber) > 0; // Goal
      case 2: return formData.leadMeasureType; // Activity
      case 3: return formData.prospectingDays.length > 0 && formData.startTime; // Schedule
      case 4: return formData.firstName && formData.email && formData.email.includes('@'); // Contact
      default: return true;
    }
  };

  const handleNext = () => {
    if (canProceed() && currentStep < totalSteps - 1) {
      // Set default daily target when activity is selected
      if (currentStep === 2 && !formData.dailyTarget) {
        const activity = LEAD_MEASURE_OPTIONS[formData.leadMeasureType];
        if (activity) {
          setFormData(prev => ({ ...prev, dailyTarget: activity.dailyDefault.toString() }));
        }
      }
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // API URLs
  const SUBMIT_LEAD_URL = 'https://90dayblitz.vercel.app/api/submit-lead';
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwK_dZSHLrb0jw-ao0b060FIkkEBCJ3dSNn7M7zDyA_WGRPxHGro8ELk83pJdlpZ8wNhQ/exec';

  const handleGeneratePlan = async () => {
    setIsGenerating(true);

    const weekPlan = generateWeekPlan();
    const activity = LEAD_MEASURE_OPTIONS[formData.leadMeasureType];

    const planData = {
      ...formData,
      weekPlan,
      aiPrompts: activity?.aiPrompts || [],
      math,
      submittedAt: new Date().toISOString(),
      planType: '5day',
    };

    // Submit to Google Sheets (backup)
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planData),
      });
    } catch (e) {
      console.error('Google Sheets error:', e);
    }

    // Submit lead - sends email, creates Monday lead, returns PDF
    try {
      const response = await fetch(SUBMIT_LEAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planData),
      });

      if (response.ok) {
        const result = await response.json();

        // Download PDF
        if (result.pdf) {
          const byteCharacters = atob(result.pdf);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `5-day-action-plan-${formData.firstName || 'plan'}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
    }

    setPlanGenerated(true);
    setIsGenerating(false);
  };

  const copyPrompt = (prompt, index) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(index);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderIntro = () => (
    <div className="step-content intro">
      <div className="intro-icon">🎯</div>
      <h1>Your 5-Day Action Plan</h1>
      <p className="intro-subtitle">
        Get a specific, day-by-day plan for the next week—plus AI prompts to help you execute every task.
      </p>

      <div className="intro-features">
        <div className="feature">
          <span className="feature-icon">📅</span>
          <div>
            <strong>Actual dates</strong>
            <p>Monday through Friday with specific tasks</p>
          </div>
        </div>
        <div className="feature">
          <span className="feature-icon">🤖</span>
          <div>
            <strong>AI prompts included</strong>
            <p>Copy-paste prompts for scripts, objections, follow-ups</p>
          </div>
        </div>
        <div className="feature">
          <span className="feature-icon">✅</span>
          <div>
            <strong>Printable checklist</strong>
            <p>Check off each task as you complete it</p>
          </div>
        </div>
      </div>

      <p className="intro-note">
        Do this every week and watch your pipeline fill up.
      </p>

      <button className="btn-primary btn-large intro-cta" onClick={handleNext}>
        Build My Plan →
      </button>

      <p className="intro-time">
        <span className="time-icon">⏱</span> Takes about 2 minutes
      </p>
    </div>
  );

  const renderGoal = () => (
    <div className="step-content">
      <h2>What's Your 90-Day Goal?</h2>
      <p className="step-description">This helps us calculate how much activity you need each week.</p>

      <div className="form-group">
        <label>I want to achieve...</label>
        <div className="goal-type-options">
          <label className={`goal-type-option ${formData.goalType === 'transactions' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="goalType"
              value="transactions"
              checked={formData.goalType === 'transactions'}
              onChange={(e) => updateFormData('goalType', e.target.value)}
            />
            <span>Transactions</span>
          </label>
          <label className={`goal-type-option ${formData.goalType === 'gci' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="goalType"
              value="gci"
              checked={formData.goalType === 'gci'}
              onChange={(e) => updateFormData('goalType', e.target.value)}
            />
            <span>GCI ($)</span>
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>
          {formData.goalType === 'transactions'
            ? 'How many transactions in the next 90 days?'
            : 'How much GCI in the next 90 days?'}
        </label>
        <div className="goal-input-wrapper">
          {formData.goalType === 'gci' && <span className="input-prefix">$</span>}
          <input
            type="number"
            className="goal-input"
            value={formData.goalNumber}
            onChange={(e) => updateFormData('goalNumber', e.target.value)}
            placeholder={formData.goalType === 'gci' ? '50000' : '6'}
          />
        </div>
      </div>

      {formData.goalNumber && parseInt(formData.goalNumber) > 0 && (
        <div className="math-preview">
          <div className="math-item">
            <span className="math-number">{math.transactionsNeeded}</span>
            <span className="math-label">transactions needed</span>
          </div>
          <div className="math-arrow">→</div>
          <div className="math-item">
            <span className="math-number">~{math.weeklyConversations}</span>
            <span className="math-label">conversations/week</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderActivity = () => (
    <div className="step-content">
      <h2>How Will You Generate Leads?</h2>
      <p className="step-description">Pick your primary prospecting activity for this week.</p>

      <div className="form-group">
        <div className="activity-options">
          {Object.entries(LEAD_MEASURE_OPTIONS).map(([key, option]) => (
            <label
              key={key}
              className={`activity-option ${formData.leadMeasureType === key ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="leadMeasureType"
                value={key}
                checked={formData.leadMeasureType === key}
                onChange={(e) => updateFormData('leadMeasureType', e.target.value)}
              />
              <div className="activity-content">
                <span className="activity-title">{option.label}</span>
                <span className="activity-desc">{option.description}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {formData.leadMeasureType && (
        <div className="form-group">
          <label>Daily target ({LEAD_MEASURE_OPTIONS[formData.leadMeasureType]?.unit})</label>
          <p className="field-hint">
            Suggested: {LEAD_MEASURE_OPTIONS[formData.leadMeasureType]?.dailyDefault} per day
          </p>
          <input
            type="number"
            className="target-input"
            value={formData.dailyTarget || LEAD_MEASURE_OPTIONS[formData.leadMeasureType]?.dailyDefault}
            onChange={(e) => updateFormData('dailyTarget', e.target.value)}
            placeholder={LEAD_MEASURE_OPTIONS[formData.leadMeasureType]?.dailyDefault.toString()}
          />
        </div>
      )}
    </div>
  );

  const renderSchedule = () => (
    <div className="step-content">
      <h2>When Will You Prospect?</h2>
      <p className="step-description">Block your prospecting time like an appointment you can't miss.</p>

      <div className="form-group">
        <label>Which days?</label>
        <div className="days-grid">
          {DAYS_OF_WEEK.map(day => (
            <label
              key={day}
              className={`day-option ${formData.prospectingDays.includes(day) ? 'selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={formData.prospectingDays.includes(day)}
                onChange={() => toggleProspectingDay(day)}
              />
              <span>{day.slice(0, 3)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Start time</label>
          <select
            value={formData.startTime}
            onChange={(e) => updateFormData('startTime', e.target.value)}
            className="time-select"
          >
            {TIME_SLOTS.map(slot => (
              <option key={slot.value} value={slot.value}>{slot.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Duration</label>
          <select
            value={formData.duration}
            onChange={(e) => updateFormData('duration', e.target.value)}
            className="time-select"
          >
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="90">1.5 hours</option>
            <option value="120">2 hours</option>
          </select>
        </div>
      </div>

      <div className="schedule-preview">
        <p className="preview-label">Your prospecting block:</p>
        <p className="preview-time">
          {formData.prospectingDays.slice(0, 3).join(', ')}{formData.prospectingDays.length > 3 ? '...' : ''} at {formData.startTime}
        </p>
      </div>
    </div>
  );

  const renderContact = () => (
    <div className="step-content">
      <h2>Where Should We Send Your Plan?</h2>
      <p className="step-description">We'll email you a printable PDF with your action plan and AI prompts.</p>

      <div className="form-group">
        <label>First name</label>
        <input
          type="text"
          value={formData.firstName}
          onChange={(e) => updateFormData('firstName', e.target.value)}
          placeholder="John"
        />
      </div>

      <div className="form-group">
        <label>Last name</label>
        <input
          type="text"
          value={formData.lastName}
          onChange={(e) => updateFormData('lastName', e.target.value)}
          placeholder="Smith"
        />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          placeholder="john@email.com"
        />
      </div>
    </div>
  );

  const renderResult = () => {
    const weekPlan = generateWeekPlan();
    const activity = LEAD_MEASURE_OPTIONS[formData.leadMeasureType];
    const totalTarget = weekPlan.filter(d => d.isActive).reduce((sum, d) => sum + d.target, 0);

    return (
      <div className="step-content result">
        <div className="result-header">
          <h1>Your 5-Day Action Plan</h1>
          <p className="result-subtitle">
            Week of {weekPlan[0]?.date} - {weekPlan[4]?.date}
          </p>
        </div>

        <div className="goal-reminder">
          <span className="goal-label">Your 90-day goal:</span>
          <span className="goal-value">
            {formData.goalType === 'gci'
              ? `$${parseInt(formData.goalNumber).toLocaleString()} GCI`
              : `${formData.goalNumber} transactions`}
          </span>
          <span className="goal-math">
            = ~{math.weeklyConversations} conversations/week
          </span>
        </div>

        <div className="week-plan">
          {weekPlan.map((day, index) => (
            <div key={index} className={`day-card ${day.isActive ? 'active' : 'inactive'}`}>
              <div className="day-header">
                <span className="day-name">{day.day}</span>
                <span className="day-date">{day.date}</span>
              </div>
              {day.isActive ? (
                <div className="day-content">
                  <div className="day-task">
                    <span className="checkbox">☐</span>
                    <div className="task-details">
                      <span className="task-time">{day.time} ({day.duration} min)</span>
                      <span className="task-action">
                        {day.target} {day.unit}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="day-content inactive">
                  <span className="day-off">Rest day</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="weekly-total">
          <span className="total-label">Weekly total:</span>
          <span className="total-value">{totalTarget} {activity?.unit}</span>
        </div>

        <div className="ai-prompts-section">
          <h2>AI Prompts to Help You Execute</h2>
          <p className="prompts-intro">
            Copy these prompts into ChatGPT, Claude, or your favorite AI to get scripts, handle objections, and write follow-ups.
          </p>

          <div className="prompts-list">
            {activity?.aiPrompts.map((prompt, index) => (
              <div key={index} className="prompt-card">
                <div className="prompt-header">
                  <span className="prompt-title">{prompt.title}</span>
                  <button
                    className={`copy-btn ${copiedPrompt === index ? 'copied' : ''}`}
                    onClick={() => copyPrompt(prompt.prompt, index)}
                  >
                    {copiedPrompt === index ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="prompt-text">{prompt.prompt}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <h3>Want weekly accountability?</h3>
          <p>At Kale Realty, we provide weekly check-ins, AI tools for follow-up, and 100% commission.</p>
          <a href="https://joinkale.com/schedule" className="btn-primary btn-large" target="_blank" rel="noopener noreferrer">
            Schedule a Call →
          </a>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (planGenerated) return renderResult();

    switch (currentStep) {
      case 0: return renderIntro();
      case 1: return renderGoal();
      case 2: return renderActivity();
      case 3: return renderSchedule();
      case 4: return renderContact();
      default: return renderIntro();
    }
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="planner-container">
      {/* Progress bar - hide on intro and result */}
      {currentStep > 0 && !planGenerated && (
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>
      )}

      <main className="planner-main">
        {renderCurrentStep()}
      </main>

      {/* Navigation */}
      {currentStep > 0 && !planGenerated && (
        <footer className="planner-footer">
          <div className="footer-content">
            <button className="btn-back" onClick={handleBack}>
              ← Back
            </button>

            {currentStep < 4 && (
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Continue →
              </button>
            )}

            {currentStep === 4 && (
              <button
                className="btn-primary btn-large"
                onClick={handleGeneratePlan}
                disabled={!canProceed() || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Get My 5-Day Plan →'}
              </button>
            )}
          </div>
        </footer>
      )}

      {/* Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&display=swap');

        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.2.0/dist/fonts/geist-sans/Geist-Regular.woff2') format('woff2');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.2.0/dist/fonts/geist-sans/Geist-Medium.woff2') format('woff2');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.2.0/dist/fonts/geist-sans/Geist-SemiBold.woff2') format('woff2');
          font-weight: 600;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.2.0/dist/fonts/geist-sans/Geist-Bold.woff2') format('woff2');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }

        .planner-container {
          --color-primary: #012f66;
          --color-primary-dark: #001d42;
          --color-primary-light: #e8f0f8;
          --color-text: #0f172a;
          --color-text-light: #475569;
          --color-text-muted: #94a3b8;
          --color-border: #e2e8f0;
          --color-bg: #f5f5f5;
          --color-bg-alt: #eeeeee;
          --color-success: #22c55e;

          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: var(--color-text);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--color-bg);
        }

        .progress-bar {
          height: 4px;
          background: var(--color-border);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .progress-fill {
          height: 100%;
          background: var(--color-primary);
          transition: width 0.3s ease;
        }

        .planner-main {
          flex: 1;
          padding: 1.5rem;
          padding-bottom: 100px;
        }

        .step-content {
          max-width: 540px;
          margin: 0 auto;
        }

        .step-content h1 {
          font-family: 'Fraunces', Georgia, serif;
          font-size: 1.75rem;
          font-weight: 400;
          margin: 0 0 0.5rem;
          color: var(--color-text);
        }

        .step-content h2 {
          font-family: 'Fraunces', Georgia, serif;
          font-size: 1.5rem;
          font-weight: 400;
          margin: 0 0 0.5rem;
          color: var(--color-text);
        }

        .step-description {
          color: var(--color-text-light);
          margin-bottom: 1.5rem;
          font-size: 1rem;
        }

        /* Intro */
        .intro {
          text-align: center;
          padding-top: 1rem;
        }

        .intro-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }

        .intro-subtitle {
          font-size: 1.1rem;
          color: var(--color-text-light);
          margin: 0 0 1.5rem;
          line-height: 1.5;
        }

        .intro-features {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          text-align: left;
          margin-bottom: 1.5rem;
        }

        .feature {
          display: flex;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: var(--color-bg-alt);
          border-radius: 10px;
        }

        .feature-icon {
          font-size: 1.25rem;
        }

        .feature strong {
          display: block;
          margin-bottom: 0.125rem;
          font-size: 0.95rem;
        }

        .feature p {
          margin: 0;
          font-size: 0.8rem;
          color: var(--color-text-light);
        }

        .intro-note {
          font-size: 0.85rem;
          color: var(--color-text-light);
          font-style: italic;
          margin-bottom: 0;
        }

        .intro-cta {
          margin-top: 1.5rem;
          padding: 1rem 2.5rem;
          font-size: 1.1rem;
        }

        .intro-time {
          margin-top: 1rem;
          font-size: 0.875rem;
          color: var(--color-text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        /* Forms */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group > label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }

        .field-hint {
          font-size: 0.875rem;
          color: var(--color-text-light);
          margin: 0 0 0.75rem;
        }

        .form-group input[type="text"],
        .form-group input[type="email"],
        .form-group input[type="number"] {
          width: 100%;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          background: white;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        /* Goal type options */
        .goal-type-options {
          display: flex;
          gap: 0.75rem;
        }

        .goal-type-option {
          flex: 1;
          padding: 1rem;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
          background: white;
        }

        .goal-type-option:hover {
          border-color: var(--color-primary);
        }

        .goal-type-option.selected {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
        }

        .goal-type-option input {
          display: none;
        }

        .goal-type-option span {
          font-weight: 600;
        }

        /* Goal input */
        .goal-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .input-prefix {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-text-light);
        }

        .goal-input {
          flex: 1;
          padding: 0.875rem 1rem;
          font-size: 1.5rem;
          font-weight: 600;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          background: white;
          font-family: inherit;
        }

        .goal-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        /* Math preview */
        .math-preview {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--color-primary-light);
          border-radius: 12px;
          margin-top: 1.5rem;
        }

        .math-item {
          text-align: center;
        }

        .math-number {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }

        .math-label {
          font-size: 0.75rem;
          color: var(--color-text-light);
        }

        .math-arrow {
          font-size: 1.25rem;
          color: var(--color-text-muted);
        }

        /* Activity options */
        .activity-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .activity-option {
          padding: 1rem;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .activity-option:hover {
          border-color: var(--color-primary);
        }

        .activity-option.selected {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
        }

        .activity-option input {
          display: none;
        }

        .activity-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .activity-title {
          font-weight: 600;
        }

        .activity-desc {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        .target-input {
          width: 120px;
          padding: 0.75rem 1rem;
          font-size: 1.25rem;
          font-weight: 600;
          text-align: center;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          background: white;
        }

        .target-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        /* Days grid */
        .days-grid {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .day-option {
          padding: 0.75rem 1rem;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
          font-weight: 500;
        }

        .day-option:hover {
          border-color: var(--color-primary);
        }

        .day-option.selected {
          border-color: var(--color-primary);
          background: var(--color-primary);
          color: white;
        }

        .day-option input {
          display: none;
        }

        /* Time select */
        .time-select {
          width: 100%;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          background: white;
          font-family: inherit;
          cursor: pointer;
        }

        .time-select:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        /* Schedule preview */
        .schedule-preview {
          padding: 1rem;
          background: var(--color-primary-light);
          border-radius: 10px;
          margin-top: 1rem;
        }

        .preview-label {
          font-size: 0.875rem;
          color: var(--color-text-light);
          margin: 0 0 0.25rem;
        }

        .preview-time {
          font-weight: 600;
          color: var(--color-primary);
          margin: 0;
        }

        /* Result page */
        .result {
          max-width: 640px;
        }

        .result-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .result-subtitle {
          color: var(--color-text-light);
          margin: 0.25rem 0 0;
        }

        .goal-reminder {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          background: var(--color-primary-light);
          border-radius: 10px;
          margin-bottom: 1.5rem;
          font-size: 0.9rem;
        }

        .goal-label {
          color: var(--color-text-light);
        }

        .goal-value {
          font-weight: 700;
          color: var(--color-primary);
        }

        .goal-math {
          color: var(--color-text-light);
        }

        /* Week plan */
        .week-plan {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .day-card {
          border: 2px solid var(--color-border);
          border-radius: 12px;
          overflow: hidden;
          background: white;
        }

        .day-card.active {
          border-color: var(--color-primary);
        }

        .day-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: var(--color-bg-alt);
        }

        .day-card.active .day-header {
          background: var(--color-primary);
          color: white;
        }

        .day-name {
          font-weight: 600;
        }

        .day-date {
          font-size: 0.875rem;
          opacity: 0.8;
        }

        .day-content {
          padding: 1rem;
        }

        .day-content.inactive {
          padding: 0.75rem 1rem;
          color: var(--color-text-muted);
          font-style: italic;
        }

        .day-task {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
        }

        .checkbox {
          font-size: 1.25rem;
          line-height: 1;
        }

        .task-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .task-time {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        .task-action {
          font-weight: 600;
        }

        .weekly-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: var(--color-primary);
          color: white;
          border-radius: 10px;
          margin-bottom: 2rem;
        }

        .total-label {
          font-weight: 500;
        }

        .total-value {
          font-size: 1.25rem;
          font-weight: 700;
        }

        /* AI Prompts */
        .ai-prompts-section {
          margin-bottom: 2rem;
        }

        .ai-prompts-section h2 {
          margin-bottom: 0.5rem;
        }

        .prompts-intro {
          color: var(--color-text-light);
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .prompts-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .prompt-card {
          border: 1px solid var(--color-border);
          border-radius: 10px;
          padding: 1rem;
          background: white;
        }

        .prompt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .prompt-title {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .copy-btn {
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
          font-weight: 600;
          border: 1px solid var(--color-primary);
          border-radius: 6px;
          background: white;
          color: var(--color-primary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .copy-btn:hover {
          background: var(--color-primary-light);
        }

        .copy-btn.copied {
          background: var(--color-success);
          border-color: var(--color-success);
          color: white;
        }

        .prompt-text {
          font-size: 0.85rem;
          color: var(--color-text-light);
          line-height: 1.5;
          margin: 0;
          background: var(--color-bg);
          padding: 0.75rem;
          border-radius: 6px;
        }

        /* CTA Section */
        .cta-section {
          text-align: center;
          padding: 1.5rem;
          background: var(--color-primary-light);
          border-radius: 12px;
        }

        .cta-section h3 {
          font-family: 'Fraunces', Georgia, serif;
          font-weight: 400;
          margin: 0 0 0.5rem;
          color: var(--color-primary-dark);
        }

        .cta-section p {
          color: var(--color-text-light);
          margin: 0 0 1rem;
          font-size: 0.9rem;
        }

        .cta-section .btn-primary {
          display: inline-block;
          text-decoration: none;
        }

        /* Footer */
        .planner-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1rem 1.5rem;
          background: var(--color-bg);
          border-top: 1px solid var(--color-border);
        }

        .footer-content {
          max-width: 540px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        /* Buttons */
        .btn-primary {
          padding: 0.875rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          color: white;
          background: var(--color-primary);
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary-dark);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary.btn-large {
          padding: 1rem 2rem;
          font-size: 1.1rem;
        }

        .btn-back {
          padding: 0.875rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          color: var(--color-text-light);
          background: transparent;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }

        .btn-back:hover {
          color: var(--color-text);
        }

        /* Responsive */
        @media (max-width: 480px) {
          .planner-main {
            padding: 1rem;
            padding-bottom: 120px;
          }

          .step-content h1 {
            font-size: 1.5rem;
          }

          .step-content h2 {
            font-size: 1.25rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .goal-type-options {
            flex-direction: column;
          }

          .days-grid {
            justify-content: center;
          }

          .math-preview {
            flex-direction: column;
            gap: 0.5rem;
          }

          .math-arrow {
            transform: rotate(90deg);
          }

          .footer-content {
            flex-direction: column;
          }

          .footer-content .btn-back {
            order: 2;
            width: 100%;
            text-align: center;
          }

          .footer-content .btn-primary {
            order: 1;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
